/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import React from 'react';

import classNames from 'classnames';

import { VisuallyHidden } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import LoadingIcon from 'bundles/cds-labs/components/LoadingIcon/LoadingIcon';

import _t from 'i18n!nls/cds-labs';

import getChatContextAttachmentCss, { classes } from './getChatContextAttachmentCss';

export type ChatContextAttachmentMediaProps = {
  type: 'media';
  size?: 'default' | 'small';
  state?: 'default' | 'loading';
  onRemove?: () => void;
  children?: React.ReactNode;
  'aria-label'?: string;
  /** File name used only for the remove button label — does not affect the group aria-label. */
  fileName?: string;
};

export type ChatContextAttachmentMetadataProps = {
  type: 'metadata';
  state?: 'default' | 'loading';
  onRemove?: () => void;
  icon?: React.ReactNode;
  label?: string;
  'aria-label'?: string;
};

export type ChatContextAttachmentProps = ChatContextAttachmentMediaProps | ChatContextAttachmentMetadataProps;

const ChatContextAttachment = React.forwardRef<HTMLDivElement, ChatContextAttachmentProps>(
  function ChatContextAttachment(props, ref) {
    if (props.type === 'media') {
      const { size = 'default', state = 'default', onRemove, children, 'aria-label': ariaLabel, fileName } = props;

      const isLoading = state === 'loading';
      const isSmall = size === 'small';

      return (
        <div
          ref={ref}
          aria-busy={isLoading}
          aria-label={ariaLabel ?? _t('Media attachment')}
          className={classNames(isSmall ? classes.mediaSmall : classes.mediaDefault, isLoading && classes.mediaLoading)}
          css={getChatContextAttachmentCss}
          role="group"
        >
          {isLoading ? (
            <LoadingIcon />
          ) : (
            <>
              <div aria-hidden="true" className={classes.mediaSlot}>
                {children}
              </div>
              {onRemove && (
                <button
                  aria-label={fileName ? _t('Remove #{name}', { name: fileName }) : _t('Remove media attachment')}
                  className={classes.closeButton}
                  type="button"
                  onClick={onRemove}
                >
                  <CloseIcon />
                </button>
              )}
            </>
          )}
        </div>
      );
    }

    // type === 'metadata'
    const { state = 'default', onRemove, icon, label, 'aria-label': ariaLabel } = props;

    const isLoading = state === 'loading';

    return (
      <div
        ref={ref}
        aria-busy={isLoading}
        aria-label={ariaLabel ?? _t('Attachment')}
        className={classNames(classes.metadataRoot, isLoading && classes.metadataLoading)}
        css={getChatContextAttachmentCss}
        role="group"
      >
        <div className={classes.metadataDetails}>
          {(icon || isLoading) && (
            <div aria-hidden="true" className={classes.metadataIconSlot}>
              {icon}
            </div>
          )}
          {isLoading ? (
            <>
              <div aria-hidden="true" className={classes.skeletonText} />
              <VisuallyHidden>{_t('Loading...')}</VisuallyHidden>
            </>
          ) : (
            label && (
              <span className={classes.metadataLabel} title={label}>
                {label}
              </span>
            )
          )}
        </div>
        {!isLoading && onRemove && (
          <button
            aria-label={label ? _t('Remove #{name}', { name: label }) : _t('Remove attachment')}
            className={classes.closeButton}
            type="button"
            onClick={onRemove}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    );
  }
);

export default ChatContextAttachment;
